import { memo } from 'react'

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DeleteOutline, DragIndicator } from '@mui/icons-material'
import { Box, IconButton, Stack, Switch, TextField, Tooltip } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { StreamSelector } from '../StreamSelector'
import type { ConfigFormValues } from '../types'

export type ViewRowProps = {
  id: string
  index: number
  repositoryId?: string
  streamEditable: boolean
  onRemove: () => void
}

const ViewRowRaw = ({ id, index, repositoryId, streamEditable, onRemove }: ViewRowProps) => {
  const { t } = useTranslation(['common', 'domain'])
  const { control } = useFormContext<ConfigFormValues>()

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  return (
    <Stack
      ref={setNodeRef}
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ py: 1, position: 'relative', zIndex: isDragging ? 1 : 'auto', opacity: isDragging ? 0.6 : 1 }}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <Tooltip title={t('domain:Workspace.Event.ConfigEditor.reorder')}>
        <IconButton
          ref={setActivatorNodeRef}
          size="small"
          sx={{ cursor: 'grab', touchAction: 'none' }}
          {...attributes}
          {...listeners}
        >
          <DragIndicator fontSize="small" />
        </IconButton>
      </Tooltip>
      <Controller
        control={control}
        name={`viewRows.${index}.name`}
        render={({ field }) => (
          <TextField
            {...field}
            size="small"
            sx={{ flex: 1 }}
            label={t('domain:Workspace.Event.ConfigEditor.cameraName')}
          />
        )}
      />
      <Box sx={{ flex: 1.5 }}>
        <Controller
          control={control}
          name={`viewRows.${index}.streamId`}
          render={({ field }) => (
            <StreamSelector
              repositoryId={repositoryId}
              kind="video"
              streamId={field.value}
              disabled={!streamEditable}
              onChange={field.onChange}
            />
          )}
        />
      </Box>
      <Controller
        control={control}
        name={`viewRows.${index}.enabled`}
        render={({ field }) => (
          <Tooltip title={t('domain:Workspace.Event.ConfigEditor.enabled')}>
            <Switch checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />
          </Tooltip>
        )}
      />
      <Tooltip title={t('delete')}>
        <IconButton onClick={onRemove}>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  )
}

export const ViewRow = memo(ViewRowRaw)
